//@ts-check
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useQueryClient } from 'react-query';
import { useProjectContext } from '@datahooks/ProjectContext';
import { useReleaseContext } from '@datahooks/ReleaseContext';

import EventEditForm from '@components/cards/events/EventEditForm';
import AttachTestEvent from '@components/cards/events/AttachTestEvent';

import PropTypes from 'prop-types';

/**
 * @typedef {{
 * openAddForm: (request: CreateEventRequest) => void,
 * openAttachEvent: (request: AttachEventRequest) => void,
 * closeAddForm: () => void,
 * closeAttachEvent: () => void,
 * }} TestEventActionsContextValue
 */

const TestEventActionsContext = createContext(/** @type {TestEventActionsContextValue | null} */ (null));

/**
 * @typedef {Object} InitialEventFormValues
 * @property {string} [event_type_id]
 * @property {string} [event_description]
 * @property {string} [start_time]
 * @property {string} [end_time]
 * @property {string} [severity]
 * @property {string} [external_link]
 */

/**
 * @typedef {Object} CreateEventRequest
 * @property {number} testTypeId
 * @property {string} testName
 * @property {string} [additionalId]
 * @property {InitialEventFormValues} [initialFormValues]
 * @property {() => void} [onCompleted]
 */

/**
 * @typedef {Object} AttachEventRequest
 * @property {number} testTypeId
 * @property {string} testName
 * @property {string} [additionalId]
 * @property {number[]} [excludeEventIds]
 * @property {() => void} [onCompleted]
 */

/**
 * Validate the given request object for creating an event, ensuring it has the required structure and types.
 *
 * @param {CreateEventRequest} request
 *
 * @returns {CreateEventRequest}
 * 
 */
const validateCreateRequest = (request) => {
  if (!request || typeof request !== 'object') {
    throw new TypeError('openAddForm requires a CreateEventRequest object');
  }

  if (request.testTypeId === undefined || typeof request.testTypeId !== 'number') {
    throw new TypeError('openAddForm requires request.testTypeId as a number');
  }

  if (request.testName === undefined || typeof request.testName !== 'string') {
    throw new TypeError('openAddForm requires request.testName as a string');

  }

  if (request.additionalId !== undefined && request.additionalId !== null && typeof request.additionalId !== 'string') {
    throw new TypeError('openAddForm request.additionalId must be a string or null if provided');
  }

  if (request.initialFormValues && typeof request.initialFormValues !== 'object') {
    throw new TypeError('openAddForm request.initialFormValues must be an object if provided');
  }

  if (request.onCompleted && typeof request.onCompleted !== 'function') {
    throw new TypeError('openAddForm request.onCompleted must be a function if provided');
  }

  return request;
};

/**
 * Validate the given request object for attaching an event, ensuring it has the required structure and types.
 * 
 * @param {AttachEventRequest} request
 *
 * @returns {AttachEventRequest}
 */
const validateAttachRequest = (request) => {
  if (!request || typeof request !== 'object') {
    throw new TypeError('openAttachEvent requires an AttachEventRequest object');
  }

  if (request.testTypeId === undefined || typeof request.testTypeId !== 'number') {
    throw new TypeError('openAttachEvent requires request.testTypeId as a number');
  }

  if (request.excludeEventIds !== undefined && (!Array.isArray(request.excludeEventIds) || request.excludeEventIds.some((eventId) => typeof eventId !== 'number'))) {
    throw new TypeError('openAttachEvent request.excludeEventIds must be an array of numbers if provided');
  }

  if (request.testName === undefined || typeof request.testName !== 'string') {
    throw new TypeError('openAttachEvent requires request.testName as a string');
  }

  if (request.additionalId !== undefined && request.additionalId !== null && typeof request.additionalId !== 'string') {
    throw new TypeError('openAttachEvent request.additionalId must be a string or null if provided');
  }

  if (request.onCompleted !== undefined && typeof request.onCompleted !== 'function') {
    throw new TypeError('openAttachEvent request.onCompleted must be a function if provided');
  }

  return request;
};

/**
 * Global provider for test event add/attach actions and modal rendering.
 * 
 * This hook should be used to add 'Add/Attach Event' functionality to any test-level component, 
 * and will render the relevant forms at the root level of the app.
 *
 * @param {Object} props
 * @param {import('react').ReactNode} props.children
 *
 * @returns {JSX.Element}
 */
export function TestEventActionsProvider({ children }) {
  const [createRequest, setCreateRequest] = useState(/** @type {CreateEventRequest | null} */ (null));
  const [attachRequest, setAttachRequest] = useState(/** @type {AttachEventRequest | null} */ (null));

  const { projectId } = useProjectContext();
  const { release } = useReleaseContext();
  const queryClient = useQueryClient();

  /**
   * Invalidates test event queries for the given test type, causing them to be refetched.
   * If testTypeId is not provided, no action is taken.
   * 
   * @param {number} testTypeId - The test type ID for which to invalidate events.
   */
  const invalidateTestEvents = useCallback((testTypeId) => {
    if (testTypeId === null || testTypeId === undefined) {
      return;
    }

    queryClient.invalidateQueries(['events', 'Test', projectId, release?.release_id, testTypeId]);
  }, [queryClient, projectId, release?.release_id]);

  /**
   * Opens the add event form with the given request details.
   * 
   * @param {CreateEventRequest} request - The request details for the event to be added.
   */
  /** @type {(request: CreateEventRequest) => void} */
  const openAddForm = useCallback((request) => {
    const validatedRequest = validateCreateRequest(request);

    setAttachRequest(null);
    setCreateRequest(validatedRequest);
  }, []);

  /**
   * Opens the attach event form with the given request details.
   * 
   * @param {AttachEventRequest} request - The request details for the event to be attached.
   */
  /** @type {(request: AttachEventRequest) => void} */
  const openAttachEvent = useCallback((request) => {
    const validatedRequest = validateAttachRequest(request);

    setCreateRequest(null);
    setAttachRequest(validatedRequest);
  }, []);

  /**
   * Closes the add event form.
   * 
   * Note: This does not trigger any query invalidation, so should only be used if the add action was cancelled. 
   * If an event was successfully added, handleEventAdded should be used instead to ensure the events list is updated.
   * 
   */
  const closeAddForm = useCallback(() => {
    setCreateRequest(null);
  }, []);

  /**
   * Closes the attach event form.
   * 
   * Note: This does not trigger any query invalidation, so should only be used if the attach action was cancelled. 
   * If an event was successfully attached, handleEventAttached should be used instead to ensure the events list is updated.
   */
  const closeAttachEvent = useCallback(() => {
    setAttachRequest(null); // Clear the attach request to close the form
  }, []);

  /**
   * Handles completion of the add event action by invalidating the relevant queries to update the events list, and then closing the form.
   * 
   * If an onCompleted callback was provided in the request, it will be called after invalidation.
   */
  const handleEventAdded = useCallback(() => {
    const completedRequest = createRequest;
    setCreateRequest(null);

    invalidateTestEvents(completedRequest?.testTypeId);

    if (completedRequest?.onCompleted) {
      completedRequest.onCompleted();
    }
  }, [createRequest, invalidateTestEvents]);

  /**
   * Handles completion of the attach event action by invalidating the relevant queries to update the events list, and then closing the form.
   * 
   * If an onCompleted callback was provided in the request, it will be called after invalidation.
   */
  const handleEventAttached = useCallback(() => {
    const completedRequest = attachRequest;
    setAttachRequest(null); // Clear the attach request to close the form

    invalidateTestEvents(completedRequest?.testTypeId);

    if (completedRequest?.onCompleted) {
      completedRequest.onCompleted();
    }
  }, [attachRequest, invalidateTestEvents]);

  /**
   * Memoises the context value to prevent unnecessary re-renders of consuming components.
   * The context value only changes if the open/close handlers change, which only happens when the requests change.
   * This ensures that components consuming this context won't re-render unless the actual handlers they use have changed.
   */
  const contextValue = useMemo(() => ({
    openAddForm,
    openAttachEvent,
    closeAddForm,
    closeAttachEvent,
  }), [openAddForm, openAttachEvent, closeAddForm, closeAttachEvent]);

  return (
    <TestEventActionsContext.Provider value={contextValue}>
      {children}

      {createRequest && (
        <EventEditForm
          isOpen={!!createRequest}
          onClose={closeAddForm}
          onSave={handleEventAdded}
          eventLevel="Test"
          testTypeId={createRequest.testTypeId}
          testName={createRequest.testName}
          additionalId={createRequest.additionalId}
          initialFormValues={createRequest.initialFormValues}
        />
      )}

      {attachRequest && (
        <AttachTestEvent
          isOpen={!!attachRequest}
          onClose={closeAttachEvent}
          onAttached={handleEventAttached}
          testTypeId={attachRequest.testTypeId}
          excludeEventIds={attachRequest.excludeEventIds}
          testName={attachRequest.testName}
          additionalId={attachRequest.additionalId}
        />
      )}
    </TestEventActionsContext.Provider>
  );
}

TestEventActionsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Trigger-only hook for opening global test event add/attach flows.
 *
 * @returns {TestEventActionsContextValue}
 */
export default function useTestEventActions() {
  const context = useContext(TestEventActionsContext);

  if (!context) {
    throw new Error('useTestEventActions must be used within TestEventActionsProvider');
  }

  return context;
}
